/**
 * Live preview of a Codira "statement card" variation.
 * Renders the template's SVG frame (seeded from /public or uploaded via the API)
 * and TYPES the headline in character-by-character (blinking caret), then pops
 * each checklist marker and types its text — mirroring render_statement_ad_to_mp4.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

// Split a headline into accent/non-accent segments on the first case-insensitive
// occurrence of `accent`.
function accentSegments(headline, accent) {
  const h = headline || "";
  const a = (accent || "").trim();
  if (!a) return [{ text: h, isAccent: false }];
  const i = h.toLowerCase().indexOf(a.toLowerCase());
  if (i === -1) return [{ text: h, isAccent: false }];
  return [
    { text: h.slice(0, i), isAccent: false },
    { text: h.slice(i, i + a.length), isAccent: true },
    { text: h.slice(i + a.length), isAccent: false },
  ].filter((s) => s.text.length > 0);
}

export default function StatementPreview({
  template,
  headline = "",
  accent = "",
  bullets = [],
  typeSpeed = "medium",
  className = "",
  animate = false,
}) {
  const cleanBullets = (bullets || []).filter((b) => (b || "").trim());
  const [bgUrl, setBgUrl] = useState(null);

  // Resolve the SVG frame: uploaded templates need an authed fetch → blob URL;
  // seeded templates are served statically from /public.
  useEffect(() => {
    if (!template) return;
    let objUrl;
    if (template.bg_svg_path) {
      api.get(`/templates/${template.id}/svg`, { responseType: "blob" })
        .then((r) => { objUrl = URL.createObjectURL(r.data); setBgUrl(objUrl); })
        .catch(() => setBgUrl(null));
    } else {
      setBgUrl(`/templates_${template.bg_key}.svg`);
    }
    return () => { if (objUrl) URL.revokeObjectURL(objUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template?.id, template?.bg_svg_path, template?.bg_key]);

  // ---- Build the character stream (headline chars, then bullet chars) ----
  const segs = accentSegments(headline || "Add a headline…", accent);
  const headlineChars = [];
  segs.forEach((s) => { for (const ch of s.text) headlineChars.push({ ch, accent: s.isAccent }); });
  let idx = headlineChars.length;
  const bulletMeta = cleanBullets.map((b) => { const start = idx; idx += b.length; return { text: b, start, len: b.length }; });
  const total = Math.max(1, idx);

  // ---- Typewriter progression ----
  const [typed, setTyped] = useState(animate ? 0 : total);
  const timer = useRef();
  useEffect(() => {
    if (!animate) { setTyped(total); return; }
    setTyped(0);
    let n = 0;
    const cps = { slow: 22, medium: 30, fast: 42 }[typeSpeed] || 30;  // matches the render
    timer.current = setInterval(() => {
      n += 1;
      setTyped(n);
      if (n >= total) clearInterval(timer.current);
    }, Math.round(1000 / cps));
    return () => clearInterval(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animate, total, headline, accent, typeSpeed, cleanBullets.join("|")]);

  if (!template) return null;
  const { width: tw, height: th, content_box: box, styling: st = {} } = template;
  if (!box) return null;

  const cqw = (px) => `${(px / tw) * 100}cqw`;
  const hSize = st.headline_size || Math.round(tw * 0.072);
  const hLh = st.headline_line_height || Math.round(hSize * 1.12);
  const bSize = st.bullet_size || Math.round(tw * 0.034);
  const bLh = st.bullet_line_height || Math.round(bSize * 1.28);
  const hGap = st.headline_gap || Math.round(th * 0.05);
  const bGap = st.bullet_gap || Math.round(th * 0.03);
  const accentColor = st.accent_color || "#ee9037";
  const textColor = st.text_color || "#f4f4f4";
  const circleColor = st.circle_color || "#3a3937";
  const checkColor = st.check_color || "#cfcecc";
  const circleD = Math.round(bSize * 1.15);

  const Caret = ({ size }) => (
    <span className="inline-block bg-brand animate-pulse align-middle"
      style={{ width: cqw(size * 0.07), height: cqw(size * 0.92), marginLeft: cqw(size * 0.04) }} />
  );

  // Headline revealed chars → colored runs
  const hlTyped = Math.min(typed, headlineChars.length);
  const runs = [];
  for (let i = 0; i < hlTyped; i++) {
    const c = headlineChars[i];
    const last = runs[runs.length - 1];
    if (last && last.accent === c.accent) last.text += c.ch;
    else runs.push({ accent: c.accent, text: c.ch });
  }
  const typingHeadline = typed < headlineChars.length;

  return (
    <div
      className={`relative w-full overflow-hidden bg-gray-100 ${className}`}
      style={{ aspectRatio: `${tw} / ${th}`, containerType: "inline-size" }}
    >
      {bgUrl && (
        <img src={bgUrl} alt={template.name}
          className="absolute inset-0 w-full h-full select-none pointer-events-none" draggable={false} />
      )}
      <div className="absolute" style={{ left: cqw(box.x), top: cqw(box.y), width: cqw(box.width), height: cqw(box.height) }}>
        {/* Headline */}
        <div className="font-inter font-bold" style={{ fontSize: cqw(hSize), lineHeight: cqw(hLh), color: textColor }}>
          {runs.map((r, i) => (
            <span key={i} style={r.accent ? { color: accentColor } : undefined}>{r.text}</span>
          ))}
          {typingHeadline && <Caret size={hSize} />}
        </div>

        {/* Checklist */}
        <div style={{ marginTop: cqw(hGap) }}>
          {bulletMeta.map((b, i) => {
            const shown = typed >= b.start;                 // check marker visible
            const revealed = Math.max(0, Math.min(b.len, typed - b.start));
            const typingThis = typed > b.start && typed < b.start + b.len;
            if (!shown) return <div key={i} style={{ marginTop: i === 0 ? 0 : cqw(bGap) }} />;
            return (
              <div key={i} className="flex items-start" style={{ marginTop: i === 0 ? 0 : cqw(bGap) }}>
                <span className="flex-shrink-0 rounded-full flex items-center justify-center"
                  style={{ width: cqw(circleD), height: cqw(circleD), background: circleColor, marginTop: cqw((bLh - circleD) / 2) }}>
                  <svg viewBox="0 0 24 24" width="62%" height="62%" fill="none"
                    stroke={checkColor} strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12.5l4 4 10-10" />
                  </svg>
                </span>
                <span className="font-inter" style={{ fontSize: cqw(bSize), lineHeight: cqw(bLh), color: textColor, marginLeft: cqw(bSize * 0.55) }}>
                  {b.text.slice(0, revealed)}
                  {typingThis && <Caret size={bSize} />}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
